import { RouterProvider, createBrowserRouter, Navigate } from 'react-router-dom';
import { c } from 'ttag';
import { APP, APP_NAME } from 'constants.js';
import useShallowEqualSelector from 'hooks/useShallowEqualSelector.js';
// Layout components (imported non-lazily)
import DataLoading from 'components/layouts/LoadingViews/DataLoading.js';
import CoreLayout from 'components/layouts/CoreLayout/index.js';
import RequireAuth from 'components/layouts/RequireAuth/index.js';
import RequireShop from 'components/layouts/RequireShop/index.js';
import 'styles/base.scss';

// The `import()` -statement DOESN'T allow expressions as arguments, only string literals. So this helper fn turns
// `() => import('X')` -fn into `{ Component }` -obj, where `Component` is the `default` export from the filepath 'X'.
// The returned object is for passing to `react-router-dom`s `createBrowserRouter` -function.
const lazyGetRoute = importer => async () => ({ Component: (await importer()).default });

const isOperator = APP_NAME === 'OPERATOR';

export const ROUTES = [
    // Operator -app's routes, that require authentication, and having selected an ELV-station
    {
        element: (
            <RequireAuth type={APP.OPERATOR} redirectTo='/login/operator'>
                <RequireShop>
                    <CoreLayout app={APP.OPERATOR} />
                </RequireShop>
            </RequireAuth>
        ),
        children: [
            {
                path: '/operator/available',
                lazy: lazyGetRoute(() => import('views/operator/Available/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_AVAILABLE` },
            },
            {
                path: '/operator/incoming',
                lazy: lazyGetRoute(() => import('views/operator/Incoming/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_INCOMING` },
            },
            {
                path: '/operator/management',
                lazy: lazyGetRoute(() => import('views/operator/Management/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_MANAGEMENT` },
            },
            {
                path: '/operator/process',
                lazy: lazyGetRoute(() => import('views/operator/Process/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_PROCESS` },
            },
            /*
            {
                path: '/operator/process/:id',
                lazy: lazyGetRoute(() => import('views/operator/Process/ProcessSingle/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_PROCESS_DETAILS` },
            },
            */
            {
                path: '/operator/recycle',
                lazy: lazyGetRoute(() => import('views/operator/Recycle/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_RECYCLE` },
            },
            {
                path: '/operator/responsibility',
                lazy: lazyGetRoute(() => import('views/operator/Responsibility/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_RESPONSIBILITY` },
            },
            {
                path: '/operator/responsibility/stats',
                lazy: lazyGetRoute(() => import('views/operator/Responsibility/Stats/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_RESPONSIBILITY_STATS` },
            },
            {
                path: '/operator/responsibility/directives',
                lazy: lazyGetRoute(() => import('views/operator/Responsibility/Directives/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_RESPONSIBILITY_DIRECTIVES` },
            },
            {
                path: '/operator/responsibility/economy',
                lazy: lazyGetRoute(() => import('views/operator/Responsibility/Economy/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_RESPONSIBILITY_ECONOMY` },
            },
            {
                path: '/operator/responsibility/evaluation',
                lazy: lazyGetRoute(() => import('views/operator/Responsibility/Evaluation/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_RESPONSIBILITY_EVALUATION` },
            },
            {
                path: '/operator/responsibility/recycling',
                lazy: lazyGetRoute(() => import('views/operator/Responsibility/Recycling/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_RESPONSIBILITY_RECYCLING` },
            },
        ],
    },
    // Operator -app's routes, that require authentication
    {
        element: (
            <RequireAuth type={APP.OPERATOR} redirectTo='/login/operator'>
                <CoreLayout app={APP.OPERATOR} />
            </RequireAuth>
        ),
        children: [
            {
                path: '/operator',
                lazy: lazyGetRoute(() => import('views/operator/Home/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_HOME` },
            },
            {
                path: '/operator/batteries/add',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Add/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_ADD` },
            },
            {
                path: '/operator/batteries/added',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Added/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_ADDED` },
            },
            {
                path: '/operator/batteries/recycled',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Recycled/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_RECYCLED` },
            },
            {
                path: '/operator/batteries/recycled/:id',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Recycled/Single/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_RECYCLED_SINGLE` },
            },
            /* // OOS for now t: esimies 2026-06-10
            {
                path: '/operator/batteries/incoming',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Incoming/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_INCOMING` },
            },
            {
                path: '/operator/batteries/incoming/:id',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Incoming/Single/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_INCOMING_SINGLE` },
            },
            */
            {
                path: '/operator/batteries/process',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Process/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_PROCESS` },
            },
            {
                path: '/operator/batteries/process/:id',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Process/Single/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_PROCESS_SINGLE` },
            },
            {
                path: '/operator/batteries/market',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Market/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_MARKET` },
            },
            {
                path: '/operator/batteries/market/:id',
                lazy: lazyGetRoute(() => import('views/operator/Batteries/Market/Single/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERIES_MARKET_SINGLE` },
            },
            {
                path: '/operator/forms/battery',
                lazy: lazyGetRoute(() => import('views/operator/Forms/Battery/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_BATTERY_FORM` },
            },
            {
                path: '/operator/orders',
                lazy: lazyGetRoute(() => import('views/operator/Orders/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_ORDERS` },
            },
            {
                path: '/operator/orders/history',
                lazy: lazyGetRoute(() => import('views/operator/Orders/History/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_ORDERS_HISTORY` },
            },
            {
                path: '/operator/profile',
                lazy: lazyGetRoute(() => import('views/operator/Profile/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_PROFILE` },
            },
            {
                path: '/operator/select-station',
                lazy: lazyGetRoute(() => import('views/operator/SelectStation/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`OPERATOR_SELECT_STATION` },
            },
        ],
    },
    // Person -app's routes, that require authentication
    {
        element: (
            <RequireAuth type={APP.PERSON} redirectTo='/login/person'>
                <CoreLayout app={APP.PERSON} />
            </RequireAuth>
        ),
        children: [
            {
                path: '/person',
                lazy: lazyGetRoute(() => import('views/person/Home/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`PERSON_HOME` },
            },
            {
                path: '/person/history',
                lazy: lazyGetRoute(() => import('views/person/History/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`PERSON_HISTORY` },
            },
            {
                path: '/person/recycle',
                lazy: lazyGetRoute(() => import('views/person/Recycle/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`PERSON_RECYCLE` },
            },
        ],
    },
    // Routes that don't require jack shit (unauthenticated)
    {
        element: <CoreLayout />,
        children: [
            {
                path: '/',
                lazy: lazyGetRoute(() => import('views/unauthenticated/ChooseApp/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_CHOOSE_APP` },
            },
            {
                path: '/forms/battery',
                lazy: lazyGetRoute(() => import('views/unauthenticated/Forms/Battery/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_BATTERY_FORM` },
            },
            /*
            {
                path: '/forms/parts',
                lazy: lazyGetRoute(() => import('views/unauthenticated/Forms/SpareParts/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_PARTS_FORM` },
            },
            */
            {
                path: '/login/dev',
                lazy: lazyGetRoute(() => import('views/person/DevLogin/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_LOGIN_DEV` },
            },
            {
                path: '/login/operator',
                lazy: lazyGetRoute(() => import('views/operator/Login/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_LOGIN_OPERATOR` },
            },
            {
                path: '/login/person',
                lazy: lazyGetRoute(() => import('views/person/Login/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_LOGIN_PERSON` },
            },
            {
                path: '/logout',
                lazy: lazyGetRoute(() => import('views/unauthenticated/Logout/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_LOGOUT` },
            },
            {
                path: '/reset/:token',
                lazy: lazyGetRoute(() => import('views/unauthenticated/PasswordReset/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_PASSWORD_RESET` },
            },
            {
                path: '/telia-login',
                lazy: lazyGetRoute(() => import('views/person/Login/TeliaReturnPage/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_LOGIN_TELIA` },
            },
            {
                path: '*',
                lazy: lazyGetRoute(() => import('views/unauthenticated/NotFound/index.js')),
                handle: { getTitle: () => c('Browser tab title').t`UNAUTHENTICATED_NOT_FOUND` },
            },
        ],
    },
    // Route aliases that work as redirects
    {
        path: '/login',
        element: <Navigate to={isOperator ? '/login/operator' : '/login/person'} />,
    },
];

export const router = createBrowserRouter(ROUTES);

const MainRouter = () => (
    <DataLoading show={useShallowEqualSelector(s => s.layout.loading)}>
        <RouterProvider router={router} />
    </DataLoading>
);

export default MainRouter;
